import { api } from '../utils/api';
import { API_ENDPOINTS } from '../constants/AppApiConstant';

/**
 * Category Service
 * Centralized API calls for category management
 */

// API Response wrapper interface
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: Record<string, string[]>;
}

// API Category interfaces (with numeric IDs)
interface ApiBilingualName {
  en: string;
  ar: string;
}

interface ApiSubcategory {
  id: number;
  name: ApiBilingualName;
  parentId: number;
  createdAt: string;
  updatedAt: string;
}

interface ApiCategory {
  id: number;
  name: ApiBilingualName;
  subcategories?: ApiSubcategory[];
  createdAt: string;
  updatedAt: string;
}

// Frontend Category interfaces (with string IDs)
export interface Category {
  id: string;
  name: {
    en: string;
    ar: string;
  };
  subcategories: Subcategory[];
  createdAt: string;
  updatedAt: string;
}

export interface Subcategory {
  id: string;
  name: {
    en: string;
    ar: string;
  };
  parentId: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Helper to unwrap API response
 */
const unwrapResponse = <T>(response: ApiResponse<T>): T => {
  if (!response || typeof response !== 'object') {
    throw new Error('Invalid API response format');
  }
  
  if (!response.success) {
    // If there are validation errors, format them nicely
    if (response.errors) {
      const errorMessages = Object.entries(response.errors)
        .flatMap(([field, messages]) => messages.map(msg => `${field}: ${msg}`))
        .join(', ');
      throw new Error(errorMessages || response.message || 'API request failed');
    }
    throw new Error(response.message || 'API request failed');
  }
  
  if (response.data === undefined || response.data === null) {
    throw new Error('API response data is missing');
  }
  
  return response.data;
};

/**
 * Convert API category (numeric IDs) to frontend category (string IDs)
 */
const convertCategory = (apiCategory: ApiCategory): Category => ({
  id: String(apiCategory.id),
  name: apiCategory.name,
  subcategories: (apiCategory.subcategories || []).map(sub => ({
    id: String(sub.id),
    name: sub.name,
    parentId: String(sub.parentId),
    createdAt: sub.createdAt,
    updatedAt: sub.updatedAt
  })),
  createdAt: apiCategory.createdAt,
  updatedAt: apiCategory.updatedAt
});

/**
 * Convert API subcategory (numeric IDs) to frontend subcategory (string IDs)
 */
const convertSubcategory = (apiSubcategory: ApiSubcategory): Subcategory => ({
  id: String(apiSubcategory.id),
  name: apiSubcategory.name,
  parentId: String(apiSubcategory.parentId),
  createdAt: apiSubcategory.createdAt,
  updatedAt: apiSubcategory.updatedAt
});

/**
 * Get all categories with nested subcategories
 * Fetches categories and subcategories separately, then merges them
 */
export const getCategories = async (): Promise<Category[]> => {
  try {
    // Fetch categories and subcategories in parallel
    const [categoriesResponse, subcategoriesResponse] = await Promise.all([
      api.get<ApiResponse<ApiCategory[]>>(API_ENDPOINTS.CATEGORIES),
      api.get<ApiResponse<ApiSubcategory[]>>(API_ENDPOINTS.SUBCATEGORIES).catch(() => {
        // If subcategories endpoint fails, return empty array
        console.warn('Failed to fetch subcategories, continuing with empty array');
        return { success: true, data: [] };
      })
    ]);
    
    const categoriesData = unwrapResponse(categoriesResponse);
    const subcategoriesData = unwrapResponse(subcategoriesResponse);
    
    // Handle case where data might be undefined or not an array
    if (!categoriesData || !Array.isArray(categoriesData)) {
      console.warn('Unexpected response format from getCategories:', categoriesData);
      return [];
    }
    
    // Convert categories
    const categories = categoriesData.map(convertCategory);
    
    // Convert subcategories
    const subcategories = (subcategoriesData || []).map(convertSubcategory);
    
    // Create a map of category IDs for quick lookup
    const categoryMap = new Map<string, Category>();
    categories.forEach(cat => {
      categoryMap.set(cat.id, { ...cat, subcategories: [] });
    });
    
    // Merge subcategories into their parent categories
    subcategories.forEach(sub => {
      const parent = categoryMap.get(sub.parentId);
      if (parent) {
        parent.subcategories.push(sub);
      } else {
        console.warn(`Subcategory ${sub.id} has invalid parentId: ${sub.parentId}`);
      }
    });
    
    // Return categories with merged subcategories
    return Array.from(categoryMap.values());
  } catch (error: any) {
    console.error('Get categories error:', error);
    const errorMessage = error?.message || 'Failed to fetch categories';
    throw new Error(errorMessage);
  }
};

/**
 * Get single category by ID
 */
export const getCategory = async (id: string): Promise<Category> => {
  try {
    const response = await api.get<ApiResponse<ApiCategory>>(`${API_ENDPOINTS.CATEGORIES}/${id}`);
    const data = unwrapResponse(response);
    return convertCategory(data);
  } catch (error: any) {
    console.error('Get category error:', error);
    const errorMessage = error?.message || 'Failed to fetch category';
    throw new Error(errorMessage);
  }
};

/**
 * Create new category
 */
export const createCategory = async (category: { name: { en: string; ar: string } }): Promise<Category> => {
  try {
    const response = await api.post<ApiResponse<ApiCategory>>(API_ENDPOINTS.CATEGORIES, category);
    const data = unwrapResponse(response);
    return convertCategory(data);
  } catch (error: any) {
    console.error('Create category error:', error);
    const errorMessage = error?.message || 'Failed to create category';
    throw new Error(errorMessage);
  }
};

/**
 * Update existing category
 */
export const updateCategory = async (id: string, category: { name: { en: string; ar: string } }): Promise<Category> => {
  try {
    const response = await api.put<ApiResponse<ApiCategory>>(`${API_ENDPOINTS.CATEGORIES}/${id}`, category);
    const data = unwrapResponse(response);
    return convertCategory(data);
  } catch (error: any) {
    console.error('Update category error:', error);
    const errorMessage = error?.message || 'Failed to update category';
    throw new Error(errorMessage);
  }
};

/**
 * Delete category
 */
export const deleteCategory = async (id: string): Promise<void> => {
  try {
    await api.delete<ApiResponse<{ message: string }>>(`${API_ENDPOINTS.CATEGORIES}/${id}`);
  } catch (error: any) {
    console.error('Delete category error:', error);
    const errorMessage = error?.message || 'Failed to delete category';
    throw new Error(errorMessage);
  }
};

/**
 * Get all subcategories
 */
export const getSubcategories = async (): Promise<Subcategory[]> => {
  try {
    const response = await api.get<ApiResponse<ApiSubcategory[]>>(API_ENDPOINTS.SUBCATEGORIES);
    const data = unwrapResponse(response);
    return data.map(convertSubcategory);
  } catch (error: any) {
    console.error('Get subcategories error:', error);
    const errorMessage = error?.message || 'Failed to fetch subcategories';
    throw new Error(errorMessage);
  }
};

/**
 * Get single subcategory by ID
 */
export const getSubcategory = async (id: string): Promise<Subcategory> => {
  try {
    const response = await api.get<ApiResponse<ApiSubcategory>>(`${API_ENDPOINTS.SUBCATEGORIES}/${id}`);
    const data = unwrapResponse(response);
    return convertSubcategory(data);
  } catch (error: any) {
    console.error('Get subcategory error:', error);
    const errorMessage = error?.message || 'Failed to fetch subcategory';
    throw new Error(errorMessage);
  }
};

/**
 * Get subcategories by category ID
 */
export const getSubcategoriesByCategory = async (categoryId: string): Promise<Subcategory[]> => {
  try {
    const response = await api.get<ApiResponse<ApiSubcategory[]>>(`${API_ENDPOINTS.CATEGORIES}/${categoryId}/subcategories`);
    const data = unwrapResponse(response);
    return data.map(convertSubcategory);
  } catch (error: any) {
    console.error('Get subcategories by category error:', error);
    const errorMessage = error?.message || 'Failed to fetch subcategories';
    throw new Error(errorMessage);
  }
};

/**
 * Create new subcategory
 * Can use either endpoint: POST /api/cms/subcategories or POST /api/cms/categories/:id/subcategories
 */
export const createSubcategory = async (
  subcategory: { name: { en: string; ar: string }; parentId: string }
): Promise<Subcategory> => {
  try {
    // Convert parentId from string to number for API
    const parentIdNum = Number(subcategory.parentId);
    
    // Use the alternative endpoint: POST /api/cms/categories/:id/subcategories
    // Backend requires parentId in both URL and body
    const response = await api.post<ApiResponse<ApiSubcategory>>(
      `${API_ENDPOINTS.CATEGORIES}/${subcategory.parentId}/subcategories`,
      { 
        name: subcategory.name,
        parentId: parentIdNum
      }
    );
    const data = unwrapResponse(response);
    return convertSubcategory(data);
  } catch (error: any) {
    console.error('Create subcategory error:', error);
    const errorMessage = error?.message || 'Failed to create subcategory';
    throw new Error(errorMessage);
  }
};

/**
 * Update existing subcategory
 */
export const updateSubcategory = async (
  id: string,
  subcategory: { name: { en: string; ar: string } }
): Promise<Subcategory> => {
  try {
    const response = await api.put<ApiResponse<ApiSubcategory>>(
      `${API_ENDPOINTS.SUBCATEGORIES}/${id}`,
      subcategory
    );
    const data = unwrapResponse(response);
    return convertSubcategory(data);
  } catch (error: any) {
    console.error('Update subcategory error:', error);
    const errorMessage = error?.message || 'Failed to update subcategory';
    throw new Error(errorMessage);
  }
};

/**
 * Delete subcategory
 */
export const deleteSubcategory = async (id: string): Promise<void> => {
  try {
    await api.delete<ApiResponse<{ message: string }>>(`${API_ENDPOINTS.SUBCATEGORIES}/${id}`);
  } catch (error: any) {
    console.error('Delete subcategory error:', error);
    const errorMessage = error?.message || 'Failed to delete subcategory';
    throw new Error(errorMessage);
  }
};
